import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "@chakra-ui/react";
import {  Box } from "@chakra-ui/react";
import "./form.css";
// Components
import MaintenanceFormMenu from "./menu/menu";
// Actions
import * as mainActions from "../../../redux/actions/main";
// Utils
import { getToastOpt } from "../../../utils/utils";
import { formatDateToUTCMadrid } from "../../../utils/formatters";

const MaintenanceForm = forwardRef(({
  primaryKey,
  primaryValue,
  primaryName,
  config,
  menuConfig,
  itemNameSingular,
  itemIsMale = true,
  actions, 
  setIsLoading,
  renderForm,
  form,
  setForm,
  isDirty,
  setIsDirty,
  isNew,
  page,
  getFormError = null,
  alterDataFormBeforeSaving = null,
  executeAfterSaving = null,
  itemIsPlural = false,
}, ref) => {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const navigate = useNavigate();
  const toast = useToast();
  // States
  const [heightForm, setHeightForm] = useState(0);
  const [initialHeightForm, setInitialHeightForm] = useState(0);
  const [initialWindowInnerHeight,] = useState(window.innerHeight);
  // Selectors
  const currentPage = useSelector(state => state.mainReducer.currentPage);
  const breadscrum = useSelector(state => !!state.mainReducer.breadscrum[currentPage] ? state.mainReducer.breadscrum[currentPage] : []);

  /*
   * Use effects
   */

  useEffect(() => {
    if (!isNew) {
      // Get data
      getData();
    }
  }, []);

  /*
   * Expose functions to be called from father
   */
  useImperativeHandle(ref, () => ({
    handleChange(evt) {
      let value;
      let fieldName;

      if (evt.isDate) {
        fieldName = evt.fieldName;
        value = formatDateToUTCMadrid(evt.value, evt.onlyDate);
      } else {
        fieldName = evt.currentTarget.name;
        const elementType = evt.target.type;    
        if (elementType === 'checkbox') {
          value = evt.currentTarget.checked;
        } else {
            const fieldType = evt.currentTarget.getAttribute(`data-type`);
            const regexPattern = evt.currentTarget.getAttribute(`data-regex`);
            const isUpperCase = evt.currentTarget.getAttribute(`data-uppercase`);
            value = evt.currentTarget.value;
            if (isUpperCase) {
              value = value.toUpperCase();
            }
            if (!!regexPattern) {
              const regex = new RegExp(regexPattern);
              if (!regex.test(value)) {
                console.log("Invalid character detected");
                return;
              }   
            }        
            if (fieldType === `int` || fieldType === `integer`) {
                value = parseInt(value);
            }
        }
      }
  
      setForm({
        ...form,
        [fieldName]: value,
      });
  
      setIsDirty(true);
    }
  }));

  /*
  * Resize control
  */

  useEffect(() => {
    if (!formRef) return;
    const h = formRef.current.clientHeight;
    setHeightForm(h);
    setInitialHeightForm(h);
  }, [formRef]); 

  useEffect(() => {
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [initialHeightForm]);

  const onResize = (e) => {
    // console.log("onResize", e);
    const diffW = Math.abs(initialWindowInnerHeight - window.innerHeight);
    const factor = window.innerHeight >= initialWindowInnerHeight ? 1 : -1;
    setHeightForm(initialHeightForm + (diffW * factor));
  };

  /*
   * Methods
   */  

  const getData = async () => {
    setIsLoading(true);
    dispatch(actions.get(primaryValue)).then((response) => {
      setIsLoading(false);
      setForm(response);
    }, err => {
      setIsLoading(false);
      toast(getToastOpt({
        title: `Error obtenint ${itemNameSingular}`,
        description: err,
        status: "error",
      }));
    });      
  };

  const handleSave = async (e) => {
    e.preventDefault(); // Avoids refreshing the page
    if (!isDirty) {
      toast(getToastOpt({
        title: `Guardar ${itemNameSingular}`,
        description: `No hi han canvis per guardar`,
        status: "warning",
        position: "top",
      }));
      onSave(false);
      return;
    }
    if (typeof getFormError === 'function') {
      const error = getFormError();
      if (!!error) {
        toast(getToastOpt({
          title: `Guardar ${itemNameSingular}`,
          description: error,
          status: "error",
          position: "top",
        }));
        onSave(false);
        return;
      }
    }
    setIsLoading(true);
    // Build submit data form
    let dataform = {};
    for (const fieldName of config.submitFields) {
      if (form[fieldName] === undefined) continue;
      dataform[fieldName] = form[fieldName];
    }
    // Alter dataform
    if (typeof alterDataFormBeforeSaving === 'function') {
      alterDataFormBeforeSaving(dataform);
    }
    // Save!
    if (isNew) {
      dispatch(actions.add(dataform)).then(
        async (response) => {
          
          // Execute after saving
          if (typeof executeAfterSaving === 'function') {
            const result = await executeAfterSaving(response.id);
            if (!result.success) {
              setIsLoading(false);
              toast(getToastOpt({
                title: `Error guardant ${itemNameSingular}`,
                description: result.msg,
                status: "error",
              }));
              onSave(true); // True because it must go back
              return;              
            }
          }

          // Continue with the execution
          setIsLoading(false);
          toast(getToastOpt({
            title: `${itemNameSingular} guardad${itemIsMale ? "o" : "a"}: ${response.id}`,
            status: "success",
            position: "top",
          }));
          onSave(true);
        },
        (err) => {
          setIsLoading(false);
          toast(getToastOpt({
            title: `Error guardant ${itemNameSingular}`,
            description: err,
            status: "error",
          }));
          onSave(false);
        }
      );
    } else {
      dispatch(actions.update(form[primaryKey], dataform)).then(
        async (response) => {

          // Execute after saving
          if (typeof executeAfterSaving === 'function') {
            const result = await executeAfterSaving(response.id);
            if (!result.success) {
              setIsLoading(false);
              toast(getToastOpt({
                title: `Error guardant ${itemNameSingular}`,
                description: result.msg,
                status: "error",
              }));
              onSave(false); // if update, it could be False
              return;              
            }
          }

          // Continue with the execution          
          setIsLoading(false);
          setIsDirty(false);
          dispatch(actions.addSelectedRow(response));
          let guardadoText;
          if (itemIsPlural) {
            guardadoText = `${itemIsMale ? "guardats" : "guardades"}`
          } else {
            guardadoText = `${itemIsMale ? "guardat" : "guardada"}`
          }
          toast(getToastOpt({
            title: `${itemNameSingular} ${guardadoText}`,
            status: "success",
            position: "top",
          }));
          onSave(true);
        },
        (err) => {
          setIsLoading(false);
          toast(getToastOpt({
            title: `Error guardant ${itemNameSingular}`,
            description: err,
            status: "error",
          }));
          onSave(false);
        }
      );
    }
  };

  const onSave = (success) => {
    if (!success) return;

    let goTo = page;
    // Remove current item of breadscrum
    let updatedBreadscrum = breadscrum;
    if (breadscrum.length > 0) {
      updatedBreadscrum = breadscrum.slice(0, -1);
      dispatch(mainActions.setBreadscrum(currentPage, updatedBreadscrum));
    }
    if (updatedBreadscrum.length > 0) {
      const item = updatedBreadscrum[updatedBreadscrum.length - 1];
      goTo = item.path;
    }
    // Goto
    navigate(`/${goTo}`);
  };

  /*
   * Render
   */
  
  return (
    <form onSubmit={handleSave} id="maintenance-form" >
        <MaintenanceFormMenu 
          config={menuConfig} 
        />
        <div className="maintenance-form-content" ref={formRef}>
          <Box className="scrollable" h={`${heightForm - 0}px`}>
              {renderForm()}
          </Box>
        </div>
    </form>
  );
});

export default MaintenanceForm;
